import {
  AccentBox,
  Alert,
  BigNumber,
  Button,
  CodeBlock,
  ColumnLayout,
  Content,
  ContextualHelp,
  ContextualHelpTrigger,
  Flex,
  Header,
  Heading,
  Icon,
  Label,
  LabeledValue,
  Link,
  LoadingSpinner,
  ModalTrigger,
  Section,
  Skeleton,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
  Text,
} from "@mittwald/flow-remote-react-components";
import { useConfig } from "@mittwald/ext-bridge/react";
import { IconRefresh } from "@tabler/icons-react";
import { useMemo, useState } from "react";
import { useProject } from "../hooks/useProject.ts";
import { usePageSpeedSettings } from "../hooks/usePageSpeedSettings.ts";
import { useUpdatePageSpeedSettings } from "../hooks/useUpdatePageSpeedSettings.ts";
import {
  useLastMeasurement,
  type PageSpeedMeasurement,
} from "../hooks/useLastMeasurement.ts";
import { useTriggerMeasurement } from "../hooks/useTriggerMeasurement.ts";
import { UpdatePageSpeedSettingsError } from "../errors/UpdatePageSpeedSettingsError.ts";
import { TriggerMeasurementError } from "../errors/TriggerMeasurementError.ts";
import {
  CATEGORIES,
  GZIP_DEFAULT_MIMES,
  PRESET_LABELS,
  type CachingConfig,
  type Category,
  type GzipConfig,
  type PageSpeedSettings,
} from "../pageSpeedConfig.ts";
import {
  deriveAnalysis,
  describeCarEquivalent,
  formatBytes,
  formatGrams,
  isSettingsEqual,
  type PageSpeedAnalysis,
} from "../pageSpeedAnalysis.ts";
import { PageSpeedCachingDialog } from "./PageSpeedCachingDialog.tsx";
import { PageSpeedGzipDialog } from "./PageSpeedGzipDialog.tsx";

const VISITOR_STEPS = [1000, 5000, 10000];
const intFormatter = new Intl.NumberFormat("de-DE");
const formatInt = (n: number): string => intFormatter.format(n);

const formatRetryAfter = (seconds: number): string => {
  if (seconds < 60) {
    return `${String(seconds)} Sekunden`;
  }
  const minutes = Math.ceil(seconds / 60);
  return `${String(minutes)} ${minutes === 1 ? "Minute" : "Minuten"}`;
};

type PendingSection =
  "caching-toggle" | "caching-save" | "gzip-toggle" | "gzip-save";

const renderCategoryHelp = (c: Category) => (
  <ContextualHelpTrigger>
    <Button />
    <ContextualHelp>
      <Heading>{c.label}</Heading>
      <Text>{c.helpText}</Text>
      <CodeBlock language="text" code={c.mimes.join("\n")} />
    </ContextualHelp>
  </ContextualHelpTrigger>
);

export const PageSpeedOptimizer = () => {
  const config = useConfig();

  const appInstallationId = config.appInstallationId;
  if (!appInstallationId) {
    throw new Error("appInstallationId is not defined");
  }

  const projectId = config.projectId;
  if (!projectId) {
    throw new Error("projectId is not defined");
  }

  const project = useProject(projectId);
  const pageSpeedSettings = usePageSpeedSettings(projectId, appInstallationId);
  const measurement = useLastMeasurement(projectId, appInstallationId);
  const updatePageSpeedSettings = useUpdatePageSpeedSettings(
    projectId,
    appInstallationId,
  );
  const triggerMeasurement = useTriggerMeasurement(
    projectId,
    appInstallationId,
  );

  const appInstallation = project.data.appInstallations.find(
    (a) => a.id === appInstallationId,
  );
  if (!appInstallation) {
    throw new Error("app installation not found in project");
  }

  const [pendingSection, setPendingSection] = useState<PendingSection | null>(
    null,
  );
  const [errorOnSubmit, setErrorOnSubmit] = useState<Error | undefined>(
    undefined,
  );
  const [measurementError, setMeasurementError] =
    useState<TriggerMeasurementError | null>(null);

  if (errorOnSubmit) {
    throw errorOnSubmit;
  }

  const current = pageSpeedSettings.data;
  const lastMeasurement = measurement.data;

  const analysis = useMemo<PageSpeedAnalysis | null>(
    () =>
      lastMeasurement?.state === "ready" && lastMeasurement.metrics
        ? deriveAnalysis(lastMeasurement.metrics, { greenHosted: true })
        : null,
    [lastMeasurement],
  );

  const isStale = useMemo(
    () =>
      lastMeasurement?.state === "ready" &&
      lastMeasurement.configSnapshot !== undefined &&
      !isSettingsEqual(lastMeasurement.configSnapshot, current),
    [lastMeasurement, current],
  );

  const hostAvailable = Boolean(appInstallation.host);

  const save = (next: PageSpeedSettings, pending: PendingSection) => {
    setPendingSection(pending);
    updatePageSpeedSettings.mutate(next, {
      onSuccess: (result) => {
        if (result.error) {
          setErrorOnSubmit(
            new UpdatePageSpeedSettingsError(result.error.message),
          );
        }
      },
      onError: (error) => {
        setErrorOnSubmit(
          new UpdatePageSpeedSettingsError(
            error instanceof Error
              ? error.message
              : "Fehler beim Aktualisieren der PageSpeed-Einstellungen",
          ),
        );
      },
      onSettled: () => {
        setPendingSection(null);
      },
    });
  };

  const toggleCaching = (enabled: boolean) => {
    save(
      { ...current, caching: { ...current.caching, enabled } },
      "caching-toggle",
    );
  };

  const toggleGzip = (enabled: boolean) => {
    save({ ...current, gzip: { ...current.gzip, enabled } }, "gzip-toggle");
  };

  const saveCaching = (caching: CachingConfig) => {
    save({ ...current, caching }, "caching-save");
  };

  const saveGzip = (gzip: GzipConfig) => {
    save({ ...current, gzip }, "gzip-save");
  };

  const handleMeasure = () => {
    setMeasurementError(null);
    triggerMeasurement.mutate(undefined, {
      onError: (error) => {
        if (error.variant === "unknown") {
          setErrorOnSubmit(error);
        } else {
          setMeasurementError(error);
        }
      },
    });
  };

  const isBusy = pendingSection !== null;
  const isMeasuring =
    triggerMeasurement.isPending || lastMeasurement?.state === "pending";

  return (
    <>
      {hostAvailable && analysis && !isStale && (
        <PotentialSummary
          analysis={analysis}
          gzipEnabled={current.gzip.enabled}
          cachingEnabled={current.caching.enabled}
          pendingSection={pendingSection}
          isBusy={isBusy}
          onEnableGzip={() => {
            toggleGzip(true);
          }}
          onEnableCaching={() => {
            toggleCaching(true);
          }}
        />
      )}

      {hostAvailable ? (
        <FootprintSection
          measurement={lastMeasurement}
          analysis={analysis}
          isStale={isStale}
          bothOptimizationsOff={
            !current.gzip.enabled && !current.caching.enabled
          }
          isMeasuring={isMeasuring}
          measurementError={measurementError}
          onMeasure={handleMeasure}
          onDismissError={() => {
            setMeasurementError(null);
          }}
        />
      ) : (
        <Section>
          <Heading>CO₂-Fußabdruck deiner Website</Heading>
          <Alert status="info">
            <Heading>Keine Domain verknüpft</Heading>
            <Content>
              <Text>
                Für diese Extension-Installation ist kein Hostname hinterlegt.
                Der CO₂-Fußabdruck kann erst gemessen werden, sobald eine Domain
                verknüpft ist. Caching und Komprimierung lassen sich unabhängig
                davon konfigurieren.
              </Text>
            </Content>
          </Alert>
        </Section>
      )}

      {hostAvailable && analysis && !isStale && (
        <MonthlyProjection
          analysis={analysis}
          gzipEnabled={current.gzip.enabled}
          cachingEnabled={current.caching.enabled}
        />
      )}

      <CompressionSection
        current={current}
        analysis={analysis}
        isStale={isStale}
        isBusy={isBusy}
        pendingSection={pendingSection}
        onToggle={toggleGzip}
        onSave={saveGzip}
      />

      <CachingSection
        current={current}
        analysis={analysis}
        isStale={isStale}
        isBusy={isBusy}
        pendingSection={pendingSection}
        onToggle={toggleCaching}
        onSave={saveCaching}
      />

      <ImageCompressionCrossSell />
    </>
  );
};

interface FootprintSectionProps {
  measurement: PageSpeedMeasurement | null;
  analysis: PageSpeedAnalysis | null;
  isStale: boolean;
  bothOptimizationsOff: boolean;
  isMeasuring: boolean;
  measurementError: TriggerMeasurementError | null;
  onMeasure: () => void;
  onDismissError: () => void;
}

const FootprintSection = ({
  measurement,
  analysis,
  isStale,
  bothOptimizationsOff,
  isMeasuring,
  measurementError,
  onMeasure,
  onDismissError,
}: FootprintSectionProps) => {
  const car = analysis
    ? describeCarEquivalent(analysis.co2PerVisitGrams)
    : null;

  const state = measurement?.state ?? "none";
  // When both optimizations are off, a stale measurement (taken while they were on)
  // is meaningless — show the neutral initial prompt instead of "Messung veraltet".
  const showInitial = state === "none" || (bothOptimizationsOff && isStale);
  const showMeasureButton =
    (state === "ready" || state === "failed") && !showInitial;

  return (
    <Section>
      <Header>
        <Heading>Datenübertragung deiner Website</Heading>
        {showMeasureButton && (
          <Button
            variant="soft"
            color="secondary"
            onPress={onMeasure}
            isDisabled={isMeasuring}
          >
            <Icon>
              <IconRefresh />
            </Icon>
            Erneut messen
            {isMeasuring && <LoadingSpinner />}
          </Button>
        )}
      </Header>

      {isStale && !bothOptimizationsOff && (
        <Alert status="warning">
          <Heading>Messung veraltet</Heading>
          <Content>
            <Text>
              Seit der letzten Messung wurden Einstellungen geändert. Bitte neu
              messen, um aktuelle Zahlen zu sehen.
            </Text>
          </Content>
        </Alert>
      )}

      {measurementError !== null && (
        <Alert status="danger">
          <Heading>
            {measurementError.variant === "rate-limit"
              ? "Zu häufige Messungen"
              : "Messung fehlgeschlagen"}
          </Heading>
          <Content>
            <Text>
              {measurementError.variant === "rate-limit" &&
              measurementError.retryAfterSeconds !== undefined
                ? `Bitte in ${formatRetryAfter(measurementError.retryAfterSeconds)} erneut versuchen.`
                : measurementError.message}
            </Text>
            <Button variant="soft" color="secondary" onPress={onDismissError}>
              Schließen
            </Button>
          </Content>
        </Alert>
      )}

      {state === "failed" && (
        <Alert status="danger">
          <Heading>Letzte Messung fehlgeschlagen</Heading>
          <Content>
            <Text>{measurement?.errorMessage ?? "Unbekannter Fehler"}</Text>
          </Content>
        </Alert>
      )}

      {state === "pending" && (
        <AccentBox>
          <Section>
            <Flex direction="row" align="center" gap="s">
              <LoadingSpinner />
              <Text>Messung läuft – das kann bis zu 90 Sekunden dauern.</Text>
            </Flex>
            <Skeleton height="120px" />
          </Section>
        </AccentBox>
      )}

      {showInitial && (
        <Alert status="info">
          <Heading>Noch nicht gemessen</Heading>
          <Content>
            <Text>
              Wir messen einmalig per PageSpeed Insights und zeigen dir, wo du
              mit Caching und Komprimierung Übertragungsgröße und CO₂ sparen
              kannst.
            </Text>
            <Button
              color="success"
              onPress={onMeasure}
              isDisabled={isMeasuring}
            >
              Jetzt messen
            </Button>
          </Content>
        </Alert>
      )}

      {state === "ready" && analysis !== null && !isStale && (
        <ColumnLayout s={[1]} m={[1, 1, 1]} gap="m">
          <AccentBox>
            <Section>
              <Text>
                <strong>1</strong> Seitenaufruf
              </Text>
              <BigNumber>
                <Text>{formatBytes(analysis.metrics.totalBytes)}</Text>
                <Text>Datenübertragung</Text>
              </BigNumber>
              <Text>
                ≈ {formatGrams(analysis.co2PerVisitGrams)} CO₂
                {car !== null && <> oder {car}</>}
              </Text>
            </Section>
          </AccentBox>
        </ColumnLayout>
      )}
    </Section>
  );
};

interface SectionProps {
  current: PageSpeedSettings;
  analysis: PageSpeedAnalysis | null;
  isStale: boolean;
  isBusy: boolean;
  pendingSection: PendingSection | null;
}

interface CompressionSectionProps extends SectionProps {
  onToggle: (enabled: boolean) => void;
  onSave: (gzip: GzipConfig) => void;
}

const CompressionSection = ({
  current,
  analysis,
  isStale,
  isBusy,
  pendingSection,
  onToggle,
  onSave,
}: CompressionSectionProps) => {
  const showAnalysis = analysis !== null && !isStale;
  const activeBytes = analysis?.metrics.activeCompressionSavedBytes ?? 0;
  const activeCo2 = analysis?.co2ActiveCompressionSavedGrams ?? 0;
  const showActive = current.gzip.enabled && showAnalysis && activeBytes > 0;
  const activeCar = describeCarEquivalent(activeCo2);

  return (
    <Section>
      <Header>
        <Heading>Komprimierung</Heading>
        {current.gzip.enabled && (
          <ModalTrigger>
            <Button variant="soft" color="secondary" isDisabled={isBusy}>
              Anpassen
            </Button>
            <PageSpeedGzipDialog initial={current.gzip} onSave={onSave} />
          </ModalTrigger>
        )}
      </Header>

      <Text>
        GZIP-Komprimierung verkleinert textbasierte Dateien beim Ausliefern um
        60 – 85 %. Seiten laden schneller, und du verbrauchst weniger
        Bandbreite.
      </Text>
      <Switch
        isSelected={current.gzip.enabled}
        onChange={onToggle}
        isDisabled={isBusy}
      >
        Komprimierung aktivieren{" "}
        <ContextualHelpTrigger>
          <Button />
          <ContextualHelp>
            <Heading>Komprimierte Standardtypen</Heading>
            <Text>
              Bereits komprimierte Formate (JPEG, PNG, WebP, MP4, PDF) sind
              bewusst nicht enthalten – erneute Komprimierung bringt nichts und
              kostet CPU.
            </Text>
            <CodeBlock language="text" code={GZIP_DEFAULT_MIMES.join("\n")} />
          </ContextualHelp>
        </ContextualHelpTrigger>
        {pendingSection === "gzip-toggle" && <LoadingSpinner />}
      </Switch>

      {showActive && (
        <Text>
          Aktuell spart das <strong>−{formatBytes(activeBytes)}</strong> (≈{" "}
          <strong>−{formatGrams(activeCo2)}</strong> CO₂
          {activeCar !== null && (
            <>
              {", "}
              {activeCar}
            </>
          )}
          ) pro Seitenaufruf.
        </Text>
      )}

      {current.gzip.enabled && current.gzip.customMimes.length > 0 && (
        <>
          <Label>Zusätzliche MIME-Types</Label>
          <Table aria-label="Zusätzliche MIME-Types">
            <TableHeader>
              <TableColumn>MIME-Type</TableColumn>
            </TableHeader>
            <TableBody>
              {current.gzip.customMimes.map((mime) => (
                <TableRow key={mime}>
                  <TableCell>{mime}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </>
      )}
    </Section>
  );
};

interface CachingSectionProps extends SectionProps {
  onToggle: (enabled: boolean) => void;
  onSave: (caching: CachingConfig) => void;
}

const CachingSection = ({
  current,
  analysis,
  isStale,
  isBusy,
  pendingSection,
  onToggle,
  onSave,
}: CachingSectionProps) => {
  const showAnalysis = analysis !== null && !isStale;
  const activeBytes = analysis?.activeCacheSavedBytes ?? 0;
  const activeCo2 = analysis?.co2ActiveCacheSavedGrams ?? 0;
  const showActive = current.caching.enabled && showAnalysis && activeBytes > 0;
  const activeCar = describeCarEquivalent(activeCo2);

  return (
    <Section>
      <Header>
        <Heading>Caching</Heading>
        {current.caching.enabled && (
          <ModalTrigger>
            <Button variant="soft" color="secondary" isDisabled={isBusy}>
              Anpassen
            </Button>
            <PageSpeedCachingDialog initial={current.caching} onSave={onSave} />
          </ModalTrigger>
        )}
      </Header>

      <Text>
        Browser-Caching weist Besucher an, statische Dateien lokal
        zwischenzuspeichern. Das greift bei jedem wiederholten Abruf – sowohl
        bei Wiederbesuchen als auch wenn derselbe Nutzer mehrere Seiten ansieht.
      </Text>
      <Switch
        isSelected={current.caching.enabled}
        onChange={onToggle}
        isDisabled={isBusy}
      >
        Caching aktivieren
        {pendingSection === "caching-toggle" && <LoadingSpinner />}
      </Switch>

      {showActive && (
        <Text>
          Aktuell werden ca. <strong>−{formatBytes(activeBytes)}</strong> (≈{" "}
          <strong>−{formatGrams(activeCo2)}</strong> CO₂
          {activeCar !== null && (
            <>
              {", "}
              {activeCar}
            </>
          )}
          ) pro wiederholtem Seitenaufruf nicht erneut geladen.
        </Text>
      )}

      {current.caching.enabled && (
        <>
          <ColumnLayout>
            {CATEGORIES.map((c) => (
              <LabeledValue key={c.key}>
                <Label>
                  {c.label} {renderCategoryHelp(c)}
                </Label>
                <Content>{PRESET_LABELS[current.caching[c.key]]}</Content>
              </LabeledValue>
            ))}
          </ColumnLayout>
          {current.caching.custom.length > 0 && (
            <>
              <Label>Eigene Regeln</Label>
              <Table aria-label="Eigene Regeln">
                <TableHeader>
                  <TableColumn>MIME-Type</TableColumn>
                  <TableColumn>Cache-Dauer</TableColumn>
                </TableHeader>
                <TableBody>
                  {current.caching.custom.map((rule) => (
                    <TableRow key={rule.mimeType}>
                      <TableCell>{rule.mimeType}</TableCell>
                      <TableCell>{PRESET_LABELS[rule.preset]}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          )}
        </>
      )}
    </Section>
  );
};

interface PotentialSummaryProps {
  analysis: PageSpeedAnalysis;
  gzipEnabled: boolean;
  cachingEnabled: boolean;
  pendingSection: PendingSection | null;
  isBusy: boolean;
  onEnableGzip: () => void;
  onEnableCaching: () => void;
}

const PotentialSummary = ({
  analysis,
  gzipEnabled,
  cachingEnabled,
  pendingSection,
  isBusy,
  onEnableGzip,
  onEnableCaching,
}: PotentialSummaryProps) => {
  const gzipBytes = !gzipEnabled ? analysis.bytesSavedPerVisit : 0;
  const gzipCo2 = !gzipEnabled ? analysis.co2SavedPerVisitGrams : 0;
  const cacheBytes = !cachingEnabled ? analysis.bytesSavedPerReturningVisit : 0;
  const cacheCo2 = !cachingEnabled
    ? analysis.co2SavedPerReturningVisitGrams
    : 0;

  const showGzip = gzipBytes > 0;
  const showCache = cacheBytes > 0;
  if (!showGzip && !showCache) return null;

  return (
    <Alert>
      <Heading>Ungenutztes Einsparpotenzial</Heading>
      <Content>
        {showGzip && (
          <Text>
            <strong>Komprimierung</strong>: −{formatBytes(gzipBytes)} (≈ −
            {formatGrams(gzipCo2)} CO₂) pro Seitenaufruf.
          </Text>
        )}
        {showCache && (
          <Text>
            <strong>Caching</strong>: −{formatBytes(cacheBytes)} (≈ −
            {formatGrams(cacheCo2)} CO₂) pro wiederholtem Seitenaufruf.
          </Text>
        )}
        {showGzip && (
          <Button color="success" onPress={onEnableGzip} isDisabled={isBusy}>
            Komprimierung aktivieren
            {pendingSection === "gzip-toggle" && <LoadingSpinner />}
          </Button>
        )}
        {showCache && (
          <Button color="success" onPress={onEnableCaching} isDisabled={isBusy}>
            Caching aktivieren
            {pendingSection === "caching-toggle" && <LoadingSpinner />}
          </Button>
        )}
      </Content>
    </Alert>
  );
};

interface MonthlyProjectionProps {
  analysis: PageSpeedAnalysis;
  gzipEnabled: boolean;
  cachingEnabled: boolean;
}

const MonthlyProjection = ({
  analysis,
  gzipEnabled,
  cachingEnabled,
}: MonthlyProjectionProps) => {
  const activeCompression = gzipEnabled
    ? analysis.co2ActiveCompressionSavedGrams
    : 0;
  const activeCache = cachingEnabled ? analysis.co2ActiveCacheSavedGrams : 0;

  const perVisit = activeCompression + activeCache;
  if (perVisit <= 0) return null;

  return (
    <Section>
      <Heading>Aktuelle Einsparung pro Monat</Heading>
      <Text>
        So viel CO₂ spart die Extension pro Monat bereits – abhängig von den
        monatlichen Seitenaufrufen.
      </Text>
      <ColumnLayout s={[1]} m={[1, 1, 1]} gap="m">
        {VISITOR_STEPS.map((visits) => {
          const total = perVisit * visits;
          const car = describeCarEquivalent(total);
          return (
            <AccentBox key={visits}>
              <Section>
                <Text>
                  <strong>{formatInt(visits)}</strong> Seitenaufrufe/Monat
                </Text>
                <BigNumber>
                  <Text>−{formatGrams(total)}</Text>
                  <Text>CO₂</Text>
                </BigNumber>
                {car !== null && <Text>≈ {car}</Text>}
              </Section>
            </AccentBox>
          );
        })}
      </ColumnLayout>
    </Section>
  );
};

const ImageCompressionCrossSell = () => (
  <Section>
    <AccentBox backgroundColor="gradient">
      <Section>
        <Heading>Noch mehr Potenzial</Heading>
        <Text>
          Bilder machen oft den größten Teil des Traffics aus. Die{" "}
          <strong>Bildkomprimierungs-Extension</strong> optimiert Bildgröße und
          Bildformat automatisch – ohne sichtbaren Qualitätsverlust.
        </Text>
        <Flex>
          <Link
            href="https://studio.mittwald.de/app/marketplace/marketplace/6bacc5ea-e14f-471f-a6be-99a1e0ac7d45"
            target="_blank"
          >
            Zur Extension
          </Link>
        </Flex>
      </Section>
    </AccentBox>
  </Section>
);
